/**
 * Activities Page
 * Activity logging and tracking across all entities
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Plus,
  Phone,
  Mail,
  Users,
  StickyNote,
  ArrowRight,
  Monitor,
  CheckSquare,
  FileText,
  Search as SearchIcon,
  Clock,
  Trash2,
  Pencil,
  BarChart3,
} from 'lucide-react';
import { ActivityType } from '@bizsolutions/shared';

interface Activity {
  id: string;
  accountId: string | null;
  contactId: string | null;
  dealId: string | null;
  ticketId: string | null;
  invoiceId: string | null;
  contractId: string | null;
  activityType: string;
  title: string;
  description: string | null;
  activityDate: string;
  durationMinutes: number | null;
  category: string | null;
  tags: string[];
  performedBy: string;
  performerName: string;
  createdAt: string;
}

interface ActivitySummaryItem {
  activityType: string;
  activityCount: number;
  totalDurationMinutes: number;
}

const ACTIVITY_TYPE_CONFIG: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  call: { icon: <Phone className="h-4 w-4" />, color: 'bg-blue-500', label: 'Call' },
  email: { icon: <Mail className="h-4 w-4" />, color: 'bg-green-500', label: 'Email' },
  meeting: { icon: <Users className="h-4 w-4" />, color: 'bg-purple-500', label: 'Meeting' },
  note: { icon: <StickyNote className="h-4 w-4" />, color: 'bg-yellow-500', label: 'Note' },
  follow_up: { icon: <ArrowRight className="h-4 w-4" />, color: 'bg-orange-500', label: 'Follow-Up' },
  demo: { icon: <Monitor className="h-4 w-4" />, color: 'bg-indigo-500', label: 'Demo' },
  task: { icon: <CheckSquare className="h-4 w-4" />, color: 'bg-teal-500', label: 'Task' },
  filing: { icon: <FileText className="h-4 w-4" />, color: 'bg-slate-500', label: 'Filing' },
  research: { icon: <SearchIcon className="h-4 w-4" />, color: 'bg-cyan-500', label: 'Research' },
  other: { icon: <Clock className="h-4 w-4" />, color: 'bg-gray-500', label: 'Other' },
};

export function ActivitiesPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [formData, setFormData] = useState({
    activityType: 'call' as string,
    title: '',
    description: '',
    activityDate: new Date().toISOString().slice(0, 16),
    durationMinutes: '',
    category: '',
    accountId: '',
  });

  // Fetch activities
  const { data: activitiesData } = useQuery({
    queryKey: ['activities', search, typeFilter, page],
    queryFn: () =>
      api.getActivities({
        search: search || undefined,
        activityType: typeFilter !== 'all' ? typeFilter : undefined,
        page,
        limit: 20,
      }),
  });
  const activities = (activitiesData?.data || []) as Activity[];
  const pagination = activitiesData?.pagination;

  // Fetch summary
  const { data: summaryData } = useQuery({
    queryKey: ['activitySummary'],
    queryFn: () => api.getActivitySummary(),
  });
  const summary = (summaryData?.data || []) as ActivitySummaryItem[];

  // Fetch accounts for the form
  const { data: accountsData } = useQuery({
    queryKey: ['accountsList'],
    queryFn: () => api.getAccounts({ limit: 100 }),
  });
  const accounts = (accountsData?.data || []) as { id: string; name: string }[];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createActivity(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['activitySummary'] });
      setIsDialogOpen(false);
      resetForm();
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.updateActivity(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['activitySummary'] });
      setIsDialogOpen(false);
      setEditingActivity(null);
      resetForm();
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteActivity(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['activitySummary'] });
    },
  });

  function resetForm() {
    setFormData({
      activityType: 'call',
      title: '',
      description: '',
      activityDate: new Date().toISOString().slice(0, 16),
      durationMinutes: '',
      category: '',
      accountId: '',
    });
  }

  function handleAdd() {
    setEditingActivity(null);
    resetForm();
    setIsDialogOpen(true);
  }

  function handleEdit(activity: Activity) {
    setEditingActivity(activity);
    setFormData({
      activityType: activity.activityType,
      title: activity.title,
      description: activity.description || '',
      activityDate: activity.activityDate ? new Date(activity.activityDate).toISOString().slice(0, 16) : '',
      durationMinutes: activity.durationMinutes?.toString() || '',
      category: activity.category || '',
      accountId: activity.accountId || '',
    });
    setIsDialogOpen(true);
  }

  function handleSubmit() {
    const payload: Record<string, unknown> = {
      activityType: formData.activityType,
      title: formData.title,
      description: formData.description || null,
      activityDate: formData.activityDate || undefined,
      durationMinutes: formData.durationMinutes ? parseInt(formData.durationMinutes) : null,
      category: formData.category || null,
    };
    if (formData.accountId) payload.accountId = formData.accountId;

    if (editingActivity) {
      updateMutation.mutate({ id: editingActivity.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  const totalActivities = summary.reduce((sum, s) => sum + s.activityCount, 0);
  const totalDuration = summary.reduce((sum, s) => sum + s.totalDurationMinutes, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Activities</h1>
          <p className="text-muted-foreground">Log and track activities across all entities</p>
        </div>
        <Button onClick={handleAdd}>
          <Plus className="h-4 w-4 mr-2" />
          Log Activity
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Total Activities</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalActivities}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Total Duration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.floor(totalDuration / 60)}h {totalDuration % 60}m
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Activity Types</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground flex items-center gap-1">
              <BarChart3 className="h-3 w-3" /> Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1">
              {summary.slice(0, 4).map((s) => (
                <Badge key={s.activityType} variant="secondary" className="text-xs">
                  {ACTIVITY_TYPE_CONFIG[s.activityType]?.label || s.activityType}: {s.activityCount}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search activities..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-8"
          />
        </div>
        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {Object.entries(ActivityType).map(([key, val]) => (
              <SelectItem key={key} value={val}>
                {ACTIVITY_TYPE_CONFIG[val]?.label || val}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Activity Timeline */}
      <div className="space-y-3">
        {activities.map((activity) => {
          const config = ACTIVITY_TYPE_CONFIG[activity.activityType] || ACTIVITY_TYPE_CONFIG.other;
          return (
            <div
              key={activity.id}
              className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className={`${config.color} text-white p-2 rounded-full shrink-0`}>
                {config.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium">{activity.title}</h3>
                    {activity.description && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {activity.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0 ml-4">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(activity)}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm('Delete this activity?')) deleteMutation.mutate(activity.id);
                      }}
                    >
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                  <Badge variant="outline" className="text-xs">
                    {config.label}
                  </Badge>
                  <span>{new Date(activity.activityDate).toLocaleString()}</span>
                  {activity.durationMinutes && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {activity.durationMinutes}m
                    </span>
                  )}
                  <span>by {activity.performerName}</span>
                  {activity.category && <Badge variant="secondary" className="text-xs">{activity.category}</Badge>}
                </div>
              </div>
            </div>
          );
        })}
        {activities.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            No activities found. Log your first activity to get started.
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * 20 + 1}-{Math.min(page * 20, pagination.total)} of {pagination.total}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pagination.totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingActivity ? 'Edit Activity' : 'Log Activity'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Activity Type</Label>
                <Select value={formData.activityType} onValueChange={(v) => setFormData({ ...formData, activityType: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ActivityType).map(([key, val]) => (
                      <SelectItem key={key} value={val}>
                        {ACTIVITY_TYPE_CONFIG[val]?.label || val}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Account</Label>
                <Select value={formData.accountId} onValueChange={(v) => setFormData({ ...formData, accountId: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((acc) => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Follow-up call with client"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Details about the activity..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Date & Time</Label>
                <Input
                  type="datetime-local"
                  value={formData.activityDate}
                  onChange={(e) => setFormData({ ...formData, activityDate: e.target.value })}
                />
              </div>
              <div>
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={formData.durationMinutes}
                  onChange={(e) => setFormData({ ...formData, durationMinutes: e.target.value })}
                  placeholder="30"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="Optional"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!formData.title || !formData.accountId || createMutation.isPending || updateMutation.isPending}
            >
              {editingActivity ? 'Update' : 'Log Activity'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
